import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { apiErrorMessage } from "@/lib/apiError";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Modal } from "@/components/ui/modal";
import { InviteAdminForm } from "@/components/admin/InviteAdminForm";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";

interface AdminItem {
  id: string;
  email: string;
  role: string;
  status: "invited" | "active" | "disabled";
  invitedByAdminId: string | null;
  createdAt: string;
}

interface AdminList {
  data: AdminItem[];
  meta: { total: number; page: number; limit: number };
}

interface RoleOption {
  role: string;
  permissions: string[];
}

type PendingAction =
  | { kind: "role"; admin: AdminItem; newRole: string }
  | { kind: "disable"; admin: AdminItem }
  | { kind: "enable"; admin: AdminItem }
  | { kind: "delete"; admin: AdminItem };

const STATUS_VARIANT: Record<
  AdminItem["status"],
  "success" | "warning" | "mute"
> = {
  active: "success",
  invited: "warning",
  disabled: "mute",
};

function fmtDate(iso: string) {
  return iso.slice(0, 10).replace(/-/g, ".");
}

function confirmCopy(p: PendingAction): {
  title: string;
  body: string;
  cta: string;
  danger: boolean;
} {
  switch (p.kind) {
    case "role":
      return {
        title: "Change role",
        body: `Change ${p.admin.email}'s role from "${p.admin.role}" to "${p.newRole}"?`,
        cta: "Change role",
        danger: false,
      };
    case "disable":
      return {
        title: "Disable user",
        body: `Disable ${p.admin.email}? They will be signed out and unable to log in until re-enabled.`,
        cta: "Disable",
        danger: true,
      };
    case "enable":
      return {
        title: "Enable user",
        body: `Re-enable ${p.admin.email}? They will be able to log in again.`,
        cta: "Enable",
        danger: false,
      };
    case "delete":
      return {
        title: "Delete user",
        body: `Permanently delete ${p.admin.email}? This removes the account and cannot be undone.`,
        cta: "Delete permanently",
        danger: true,
      };
  }
}

export function UsersPage() {
  const navigate = useNavigate();
  const { user, can } = useAuth();
  const manage = can("admin:manage");

  const [items, setItems] = useState<AdminItem[]>([]);
  const [roles, setRoles] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [inviteOpen, setInviteOpen] = useState(false);
  const [pending, setPending] = useState<PendingAction | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user && !can("admin:view")) navigate({ to: "/dpps" });
  }, [user, can, navigate]);

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    api
      .get<AdminList>("/admin/admins", { params: { page: 1, limit: 100 } })
      .then(({ data }) => {
        setItems(data.data);
        setLoading(false);
      })
      .catch(() => {
        setError("Failed to load users");
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    load();
    api
      .get<{ data: RoleOption[] }>("/admin/admins/roles")
      .then(({ data }) => setRoles(data.data.map((r) => r.role)))
      .catch(() => undefined);
  }, [load]);

  async function runPending() {
    if (!pending) return;
    setBusy(true);
    setError("");
    try {
      if (pending.kind === "role") {
        await api.patch(`/admin/admins/${pending.admin.id}`, {
          role: pending.newRole,
        });
      } else if (pending.kind === "disable") {
        await api.patch(`/admin/admins/${pending.admin.id}`, {
          status: "disabled",
        });
      } else if (pending.kind === "enable") {
        await api.patch(`/admin/admins/${pending.admin.id}`, {
          status: "active",
        });
      } else {
        await api.delete(`/admin/admins/${pending.admin.id}`);
      }
      setPending(null);
      load();
    } catch (err: unknown) {
      setError(apiErrorMessage(err, "Action failed"));
    } finally {
      setBusy(false);
    }
  }

  async function resendInvite(id: string) {
    setError("");
    try {
      await api.post(`/admin/admins/${id}/resend-invite`);
    } catch {
      setError("Failed to resend invite");
    }
  }

  const copy = pending ? confirmCopy(pending) : null;

  return (
    <div className="adm__body">
      <div className="adm__page-head">
        <div className="adm__title-stack">
          <div className="meta">↗ ADMIN CONSOLE USERS</div>
          <h1 className="h1" style={{ fontSize: 36, margin: 0 }}>
            Users
          </h1>
        </div>
        {manage && (
          <Button size="sm" onClick={() => setInviteOpen(true)}>
            + Invite user
          </Button>
        )}
      </div>

      {error && (
        <div
          style={{
            fontFamily: "var(--f-mono)",
            fontSize: 12,
            color: "var(--danger)",
            border: "1px solid var(--danger)",
            padding: "10px 14px",
            marginBottom: 16,
          }}
        >
          {error}
        </div>
      )}

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Email</TableHead>
            <TableHead>Role</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Added</TableHead>
            {manage && <TableHead>Actions</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={manage ? 5 : 4} style={{ padding: 32 }}>
                Loading…
              </TableCell>
            </TableRow>
          ) : items.length === 0 ? (
            <TableRow>
              <TableCell colSpan={manage ? 5 : 4} style={{ padding: 32 }}>
                No users
              </TableCell>
            </TableRow>
          ) : (
            items.map((a) => {
              const isSuper = a.role === "superadmin";
              const isSelf = a.id === user?.id;
              const roleEditable = manage && !isSuper && !isSelf;
              return (
                <TableRow key={a.id}>
                  <TableCell>
                    <span className="mono">{a.email}</span>
                  </TableCell>
                  <TableCell>
                    {roleEditable ? (
                      <select
                        className="select"
                        value={a.role}
                        onChange={(e) =>
                          setPending({
                            kind: "role",
                            admin: a,
                            newRole: e.target.value,
                          })
                        }
                        style={{
                          padding: "6px 10px",
                          fontSize: 12,
                          appearance: "none",
                        }}
                      >
                        {roles.map((r) => (
                          <option key={r} value={r}>
                            {r}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span
                        className="mono"
                        style={{
                          textTransform: "uppercase",
                          fontSize: 11,
                          letterSpacing: "0.1em",
                        }}
                      >
                        {a.role}
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant={STATUS_VARIANT[a.status]}>{a.status}</Badge>
                  </TableCell>
                  <TableCell className="mono">{fmtDate(a.createdAt)}</TableCell>
                  {manage && (
                    <TableCell>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        {a.status === "invited" && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => resendInvite(a.id)}
                          >
                            Resend
                          </Button>
                        )}
                        {!isSuper && !isSelf && (
                          <>
                            {a.status === "disabled" ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() =>
                                  setPending({ kind: "enable", admin: a })
                                }
                              >
                                Enable
                              </Button>
                            ) : (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() =>
                                  setPending({ kind: "disable", admin: a })
                                }
                              >
                                Disable
                              </Button>
                            )}
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() =>
                                setPending({ kind: "delete", admin: a })
                              }
                            >
                              Delete
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>

      <Modal
        open={inviteOpen}
        title="Invite user"
        onClose={() => setInviteOpen(false)}
      >
        <InviteAdminForm onSuccess={load} />
      </Modal>

      <Modal
        open={pending !== null}
        title={copy?.title ?? ""}
        onClose={() => (busy ? undefined : setPending(null))}
      >
        <div style={{ fontSize: 13, color: "var(--ink-soft)", lineHeight: 1.5 }}>
          {copy?.body}
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 10,
            marginTop: 24,
          }}
        >
          <Button
            variant="ghost"
            size="sm"
            disabled={busy}
            onClick={() => setPending(null)}
          >
            Cancel
          </Button>
          <Button
            variant={copy?.danger ? "destructive" : "default"}
            size="sm"
            disabled={busy}
            onClick={runPending}
          >
            {busy ? "Working…" : (copy?.cta ?? "Confirm")}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
